'use client'

import useTracker from '@/hooks/useTracker'
import {
  useGetMaxDaysPickerConfigQuery,
  useGetStoreOrderQuery,
  useLazyGetPageDataQuery,
  useLazyGetThemeQuery,
} from '@/services/local'
import { useGetFeatureTogglesQuery } from '@/services/oms'
import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect } from 'react'

import FrameConnector from '@/containers/FrameConnector'
import { TailwindThemeProvider } from '@/contexts/TailwindThemeProvider'
import {
  setDefaultPageData,
  setDefaultThemeSettings,
} from '@/redux/customisation'
import { setCartName, setDetailMenuSite, setListOfMenuSite } from '@/redux/meta'
import { initConsoleErrorTracking } from '@/utils/newrelicLogger'
import { consumeQueryParam, getPageNameFromPathName } from '@/utils/routes'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '@/redux/store'

function PageClient({
  children,
  isBot,
  menuData,
  menuDetailsInfo,
  mode,
  defaultPageData,
  cartName,
  defaultThemeData,
}: {
  children: React.ReactNode
  isBot: boolean
  menuData?: Oms.V4ApiResponse<Oms.ListOfMenuSite>
  menuDetailsInfo?: Oms.V4ApiResponse<Oms.MetaDataMenuSite>
  mode: 'live' | 'preview'
  defaultPageData:
    | Oms.V4ApiResponse<Customisation.SectionSettingData>
    | undefined
  defaultThemeData: Oms.V4ApiResponse<Customisation.Theme> | undefined
  cartName?: string | null
}) {
  const searchParams = useSearchParams()
  const { notifyPageView } = useTracker()
  const pathname = usePathname()
  const dispatch = useDispatch()
  const customisationState = useSelector(
    (state: RootState) => state['@customisation']
  )

  const previewToken = searchParams?.get('previewToken')
  const referrer = searchParams?.get('referrer')

  // Combined effect for dispatching all initial data
  useEffect(() => {
    if (menuData) {
      dispatch(setListOfMenuSite(menuData))
    }
    if (menuDetailsInfo) {
      dispatch(
        setDetailMenuSite({
          ...menuDetailsInfo,
          data: { ...menuDetailsInfo.data, mode },
        })
      )
    }
    if (cartName) {
      dispatch(setCartName(cartName))
    }
    if (defaultPageData) {
      dispatch(setDefaultPageData(defaultPageData))
    }
    if (defaultThemeData) {
      dispatch(setDefaultThemeSettings(defaultThemeData))
    }
  }, [
    dispatch,
    menuData,
    menuDetailsInfo,
    mode,
    cartName,
    defaultPageData,
    defaultThemeData,
  ])

  useEffect(() => {
    const accessToken = consumeQueryParam('accessToken')

    if (
      typeof window !== 'undefined' &&
      !window.localStorage.getItem('accessToken') &&
      accessToken &&
      referrer
    ) {
      window.localStorage.setItem('accessToken', accessToken)
    }
  }, [])

  // Initialize console error tracking
  useEffect(() => {
    initConsoleErrorTracking()
  }, [])

  // Track page view
  useEffect(() => {
    if (!isBot) {
      notifyPageView()
    }
  }, [notifyPageView, isBot])

  useGetStoreOrderQuery(menuDetailsInfo?.data?.storeOrderId, {
    skip:
      !menuDetailsInfo?.data?.storeOrderId || pathname?.includes('checkout'),
  })

  const { isSuccess: fetchedFeatureToggles } = useGetFeatureTogglesQuery(
    menuDetailsInfo?.data?.menu?.id,
    {
      skip: !menuDetailsInfo?.data?.menu?.id,
    }
  )

  useGetMaxDaysPickerConfigQuery(cartName as string, {
    skip: !cartName || !fetchedFeatureToggles,
  })

  // Initialize lazy queries
  const [getTheme, { data: themeResp }] = useLazyGetThemeQuery()
  const [getPageData, { data: pageDataResp }] = useLazyGetPageDataQuery()

  useEffect(() => {
    if (menuDetailsInfo?.data?.menu?.id && previewToken) {
      getTheme({
        menuId: menuDetailsInfo?.data?.menu?.id,
        previewToken,
      })

      getPageData({
        menuId: menuDetailsInfo?.data?.menu?.id,
        previewToken,
        pageName: pathname ? getPageNameFromPathName(pathname) : '',
      })
    }
  }, [menuDetailsInfo, previewToken, pathname, getTheme, getPageData])

  // Use theme data from customisation slice when available, otherwise fallback to fetched data
  const effectiveThemeData =
    customisationState.data.previewMode &&
    customisationState.data.themeSettingData.selectedPreset
      ? ({
          data: {
            themeData: {
              siteId: customisationState.data.siteId,
              themeSettingData: customisationState.data.themeSettingData,
              siteSettingData: customisationState.data.siteSettingData,
            },
          },
        } as Oms.V4ApiResponse<Customisation.Theme>)
      : themeResp || defaultThemeData

  // Use page data from customisation slice when available, otherwise fallback to fetched data
  const effectivePageData =
    customisationState.data.previewMode &&
    customisationState.data.sectionSettingData._id
      ? ({
          data: customisationState.data.sectionSettingData,
        } as Oms.V4ApiResponse<Customisation.SectionSettingData>)
      : pageDataResp

  return (
    <TailwindThemeProvider
      themeData={effectiveThemeData}
      pageData={effectivePageData}
      defaultPageData={defaultPageData}
    >
      <div className="bg-background min-h-screen">
        <FrameConnector previewMode={Boolean(previewToken)}>
          {children}
        </FrameConnector>
      </div>
    </TailwindThemeProvider>
  )
}

export default PageClient
